import type { ReasonNode } from './types'

export interface Pos {
  x: number
  y: number
}

/**
 * Animated node positions.
 *
 * The radial layout sizes each subtree's angular wedge by its leaf count, so
 * adding one node re-proportions every wedge and *every existing node moves*.
 * That is correct — it is what keeps a growing graph from crowding — but
 * applied instantly it means the whole canvas twitches on every arrival, which
 * is what made the first load read as chaos next to a replay, where the layout
 * is already final and nodes simply grow into place.
 *
 * So `layout()` writes targets and this eases toward them. A new node snaps
 * straight to its position (its strand is already animating it into being;
 * sliding it as well would fight the growth cone), and only *subsequent*
 * reshuffles are eased. The result is a graph that quietly makes room for what
 * arrives instead of rearranging itself in front of you.
 *
 * Deliberately a plain singleton rather than store state: it updates every
 * frame while settling, and pushing 300 positions through the store at 60 fps
 * to drive an animation nothing else needs would be pure overhead.
 */
class LayoutMotion {
  private pos = new Map<string, Pos>()

  /** Bumped each frame while anything is still in motion; keys render caches. */
  epoch = 0
  /** True while at least one node is still travelling to its target. */
  moving = false
  /** A snap landed since the last step; see `snap`. */
  private snapped = false

  /** Rendered position, which may lag the layout target. */
  at(n: ReasonNode): Pos {
    return this.pos.get(n.id) ?? n
  }

  reset() {
    this.pos.clear()
    this.moving = false
    this.epoch++
  }

  /**
   * Put these nodes at their targets now, with no easing.
   *
   * Direct manipulation must not lag the pointer. At the 340 ms constant below,
   * a node being dragged trails the cursor by most of the drag and arrives
   * after it stops, which reads as the graph resisting you rather than as the
   * graph settling.
   *
   * The `snapped` flag is what keeps the renderer honest: its strand geometry
   * cache is keyed on this epoch only while `moving`, and falls back to the
   * graph revision otherwise — which a drag deliberately does not bump. Without
   * counting a snap as movement for the frame it lands in, the node would move
   * and its strand would stay drawn between the endpoints it had before.
   */
  snap(nodes: Record<string, ReasonNode>, ids: Iterable<string>) {
    for (const id of ids) {
      const n = nodes[id]
      if (!n) continue
      const cur = this.pos.get(id)
      if (cur) {
        cur.x = n.x
        cur.y = n.y
      } else {
        this.pos.set(id, { x: n.x, y: n.y })
      }
    }
    this.snapped = true
    this.moving = true
    this.epoch++
  }

  /** Advance every node toward its layout target. Called once per frame. */
  step(nodes: Record<string, ReasonNode>, order: string[], dt: number) {
    const a = 1 - Math.exp(-dt / 340)
    let moving = false

    for (const id of order) {
      const n = nodes[id]
      if (!n) continue
      const cur = this.pos.get(id)
      if (!cur) {
        this.pos.set(id, { x: n.x, y: n.y })
        continue
      }
      const dx = n.x - cur.x
      const dy = n.y - cur.y
      // Snap inside a third of a pixel — otherwise `moving` never clears and
      // the geometry caches rebuild forever.
      if (dx * dx + dy * dy < 0.12) {
        cur.x = n.x
        cur.y = n.y
        continue
      }
      cur.x += dx * a
      cur.y += dy * a
      moving = true
    }

    if (this.pos.size > order.length) {
      const live = new Set(order)
      for (const id of [...this.pos.keys()]) if (!live.has(id)) this.pos.delete(id)
    }

    // A snap counts as movement for exactly the frame that follows it, so the
    // renderer rebuilds its strand geometry around the new endpoints once and
    // then goes back to the cheap revision-keyed cache.
    this.moving = moving || this.snapped
    this.snapped = false
    if (moving) this.epoch++
  }
}

export const motion = new LayoutMotion()
